import * as React from "react";
import { cn } from "@/lib/utils";

import "./tabs.css";

type TabsContextValue = {
  activation: "automatic" | "manual";
  baseId: string;
  onValueChange: (value: string) => void;
  orientation: "horizontal" | "vertical";
  value: string;
};

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(componentName: string) {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error(`${componentName} must be rendered inside Tabs`);
  }
  return context;
}

function valueId(value: string) {
  return value.replace(/[^a-zA-Z0-9_-]/g, "-");
}

type TabsProps = React.HTMLAttributes<HTMLDivElement> & {
  activation?: "automatic" | "manual";
  onValueChange: (value: string) => void;
  orientation?: "horizontal" | "vertical";
  value: string;
};

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      activation = "automatic",
      children,
      onValueChange,
      orientation = "horizontal",
      value,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const baseId = `tabs-${generatedId.replace(/:/g, "")}`;

    return (
      <TabsContext.Provider
        value={{ activation, baseId, onValueChange, orientation, value }}
      >
        <div ref={ref} data-orientation={orientation} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  },
);
Tabs.displayName = "Tabs";

export const TabsList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, onKeyDown, ...props }, ref) => {
  const { activation, orientation } = useTabsContext("TabsList");

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;

    const inheritedDirection =
      event.currentTarget.dir ||
      event.currentTarget.closest<HTMLElement>("[dir]")?.dir ||
      event.currentTarget.ownerDocument.documentElement.dir ||
      event.currentTarget.ownerDocument.defaultView?.getComputedStyle(
        event.currentTarget,
      ).direction;
    const isHorizontalRtl =
      orientation === "horizontal" && inheritedDirection === "rtl";
    const previousKey =
      orientation === "horizontal"
        ? isHorizontalRtl
          ? "ArrowRight"
          : "ArrowLeft"
        : "ArrowUp";
    const nextKey =
      orientation === "horizontal"
        ? isHorizontalRtl
          ? "ArrowLeft"
          : "ArrowRight"
        : "ArrowDown";
    if (![previousKey, nextKey, "Home", "End"].includes(event.key)) return;

    const tabs = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]:not(:disabled)',
      ),
    );
    if (tabs.length === 0) return;

    const currentIndex = tabs.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    let nextIndex = currentIndex;
    if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else if (event.key === nextKey)
      nextIndex = (currentIndex + 1) % tabs.length;
    else nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;

    event.preventDefault();
    tabs[nextIndex]?.focus();
    if (activation === "automatic") tabs[nextIndex]?.click();
  };

  return (
    <div
      ref={ref}
      role="tablist"
      aria-orientation={orientation}
      className={cn("tabs-list", className)}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
});
TabsList.displayName = "TabsList";

type TabsTriggerProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value"
> & {
  value: string;
};

export const TabsTrigger = React.forwardRef<
  HTMLButtonElement,
  TabsTriggerProps
>(({ className, onClick, value, ...props }, ref) => {
  const context = useTabsContext("TabsTrigger");
  const selected = context.value === value;
  const idPart = valueId(value);

  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={`${context.baseId}-tab-${idPart}`}
      aria-controls={`${context.baseId}-panel-${idPart}`}
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      className={cn("tabs-trigger", className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) context.onValueChange(value);
      }}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

type TabsPanelProps = React.HTMLAttributes<HTMLDivElement> & {
  value: string;
};

export const TabsPanel = React.forwardRef<HTMLDivElement, TabsPanelProps>(
  ({ className, value, ...props }, ref) => {
    const context = useTabsContext("TabsPanel");
    const selected = context.value === value;
    const idPart = valueId(value);

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${context.baseId}-panel-${idPart}`}
        aria-labelledby={`${context.baseId}-tab-${idPart}`}
        hidden={!selected}
        tabIndex={0}
        className={cn("tabs-panel", className)}
        {...props}
      />
    );
  },
);
TabsPanel.displayName = "TabsPanel";
